import type { Identifier } from "ra-core";
import {
  buildLbsClientContextLinks,
  resolveCompanyOwnedEmailStorage,
  type LbsBillingAddress,
} from "@/modules/clients/clientContextLinks";
import { splitClientFullName } from "@/modules/clients/clientFormUtils";
import {
  cleanChannelFormValues,
  formValuesToEmailJsonb,
  formValuesToPhoneJsonb,
  getPrimaryChannelValue,
} from "@/modules/clients/clientChannels";
import {
  cleanSocialLinksForSave,
  findLinkedinUrl,
} from "@/modules/clients/clientSocialLinks";
import type { ClientSocialLinkValue } from "@/modules/clients/clientSocialLinks";
import { LBS_CLIENT_STATUS } from "@/app/navigation";
import type { ClientChannelFormValue } from "@/modules/clients/clientChannels";
import {
  coerceUsStateAbbrForSave,
  normalizeCityForSave,
} from "@/lib/usStateAbbr";

export type LbsClientUpsertInput = {
  organizationMemberId: Identifier;
  /** When editing an existing client, pass the company id explicitly. */
  companyId?: Identifier;
  /** When editing, pass the current primary contact id. */
  primaryContactId?: Identifier;
  /** When true on edit, only link primary_contact_id — do not mutate contact body. */
  linkPrimaryContactOnly?: boolean;
  primary: {
    fullName: string;
    emails?: ClientChannelFormValue[];
    phones?: ClientChannelFormValue[];
    address?: string;
  };
  business: {
    name: string;
    emails?: ClientChannelFormValue[];
    phones?: ClientChannelFormValue[];
    website?: string;
    sector?: string;
    socialLinks?: ClientSocialLinkValue[];
    address?: string;
    city?: string;
    stateAbbr?: string;
    zipcode?: string;
    country?: string;
    notes?: string;
    size?: 1 | 10 | 50 | 250 | 500 | null;
    revenue?: string;
    taxIdentifier?: string;
    linkedinUrl?: string;
  };
  billing: {
    sameAsBusiness: boolean;
    address?: string;
    city?: string;
    stateAbbr?: string;
    zipcode?: string;
    country?: string;
    invoiceSameAsPrimary: boolean;
    invoiceContactName?: string;
    invoiceEmail?: string;
    invoicePhone?: string;
    /** Designated billing contacts; undefined leaves the saved list untouched. */
    contactIds?: Identifier[];
  };
};

export type LbsClientUpsertResult = {
  company_id: Identifier;
  contact_id: Identifier | null;
  created: boolean;
};

export const hasPrimaryContactInput = (
  input: Pick<LbsClientUpsertInput, "primaryContactId" | "primary">,
): boolean => {
  if (input.primaryContactId != null) return true;
  if (input.primary.fullName?.trim()) return true;
  if (getPrimaryChannelValue(input.primary.emails)) return true;
  if (getPrimaryChannelValue(input.primary.phones)) return true;
  return false;
};

export const hasPrimaryContactDraft = (
  values: Pick<
    import("@/modules/clients/ClientCreateForm").ClientCreateFormValues,
    "primary_full_name" | "primary_email" | "primary_phone"
  >,
): boolean =>
  Boolean(
    values.primary_full_name?.trim() ||
      values.primary_email?.trim() ||
      values.primary_phone?.trim(),
  );

export type QuickCreateClientInput = {
  businessName: string;
  contactName: string;
  email?: string;
  phone?: string;
};

export const buildQuickClientUpsertInput = (
  values: QuickCreateClientInput,
  organizationMemberId: Identifier,
): LbsClientUpsertInput => {
  const emails = values.email?.trim()
    ? [{ value: values.email.trim(), type: "Work" as const, isPrimary: true }]
    : [];
  const phones = values.phone?.trim()
    ? [{ value: values.phone.trim(), type: "Work" as const, isPrimary: true }]
    : [];

  return {
    organizationMemberId,
    primary: {
      fullName: values.contactName.trim(),
      emails,
      phones,
    },
    business: {
      name: values.businessName.trim(),
    },
    billing: {
      sameAsBusiness: true,
      invoiceSameAsPrimary: true,
    },
  };
};

export const normalizeWebsite = (website?: string | null) => {
  const trimmed = website?.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
};

export const buildCompanyPayloadFromUpsert = (
  input: LbsClientUpsertInput,
  existingLinks?: string[] | null,
) => {
  const billingAddress: LbsBillingAddress | undefined = input.billing
    .sameAsBusiness
    ? undefined
    : {
        address: input.billing.address?.trim() || undefined,
        city: normalizeCityForSave(input.billing.city) || undefined,
        stateAbbr:
          coerceUsStateAbbrForSave(input.billing.stateAbbr) || undefined,
        zipcode: input.billing.zipcode?.trim() || undefined,
        country: input.billing.country?.trim() || undefined,
      };

  const companyEmails = formValuesToEmailJsonb(input.business.emails);
  const companyPhones = formValuesToPhoneJsonb(input.business.phones);
  const companySocialLinks = cleanSocialLinksForSave(
    input.business.socialLinks,
  );
  const ownedEmailStorage = resolveCompanyOwnedEmailStorage(
    companyEmails,
    existingLinks,
  );

  return {
    name: input.business.name.trim(),
    phone_number: getPrimaryChannelValue(input.business.phones) || null,
    website: normalizeWebsite(input.business.website),
    sector: input.business.sector?.trim() || null,
    linkedin_url: normalizeWebsite(
      input.business.linkedinUrl ?? findLinkedinUrl(companySocialLinks),
    ),
    address: input.business.address?.trim() || null,
    city: normalizeCityForSave(input.business.city),
    state_abbr: coerceUsStateAbbrForSave(input.business.stateAbbr),
    zipcode: input.business.zipcode?.trim() || null,
    country: input.business.country?.trim() || null,
    description: input.business.notes?.trim() || null,
    size: input.business.size ?? undefined,
    revenue: input.business.revenue?.trim() || null,
    tax_identifier: input.business.taxIdentifier?.trim() || null,
    organization_member_id: input.organizationMemberId,
    ...(input.billing.contactIds !== undefined
      ? { billing_contact_ids: input.billing.contactIds.map(Number) }
      : {}),
    context_links: buildLbsClientContextLinks(
      {
        businessEmail: ownedEmailStorage.businessEmail,
        companyEmails: ownedEmailStorage.companyEmails,
        companyPhones,
        billingSameAsBusiness: input.billing.sameAsBusiness,
        billingAddress,
        invoiceSameAsPrimary: input.billing.invoiceSameAsPrimary,
        invoiceContactName: input.billing.invoiceContactName,
        invoiceEmail: input.billing.invoiceEmail,
        invoicePhone: input.billing.invoicePhone,
        companySocialLinks,
      },
      existingLinks,
    ),
  };
};

export type ContactUpsertMode = "create" | "update";

const buildContactFormFields = (
  input: LbsClientUpsertInput,
  companyId: Identifier,
) => {
  const { firstName, lastName } = splitClientFullName(input.primary.fullName);
  return {
    first_name: firstName,
    last_name: lastName || firstName,
    company_id: companyId,
    email_jsonb: formValuesToEmailJsonb(input.primary.emails),
    phone_jsonb: formValuesToPhoneJsonb(input.primary.phones),
    address: input.primary.address?.trim() || null,
    organization_member_id: input.organizationMemberId,
  };
};

/** Fields managed by the LBS client form — omitted on update when not in the form. */
export const LBS_CLIENT_FORM_UNMANAGED_CONTACT_FIELDS = [
  "lead_source",
  "interested_service",
  "lead_source_other",
  "referred_by_contact_id",
  "referred_by_company_id",
  "linkedin_url",
  "tags",
  "first_seen",
  "status",
] as const;

export const buildContactPayloadFromUpsert = (
  input: LbsClientUpsertInput,
  companyId: Identifier,
  mode: ContactUpsertMode = "create",
) => {
  const formFields = buildContactFormFields(input, companyId);

  if (mode === "update") {
    return {
      ...formFields,
      last_seen: new Date().toISOString(),
    };
  }

  const now = new Date().toISOString();
  return {
    ...formFields,
    status: LBS_CLIENT_STATUS,
    lead_stage: "won",
    snooze_until: "2099-12-31T00:00:00+00:00",
    first_seen: now,
    last_seen: now,
    tags: [],
  };
};

const singleChannelFromValue = (value?: string) =>
  value?.trim()
    ? [{ value: value.trim(), type: "Work" as const, isPrimary: true }]
    : [];

export const clientCreateFormValuesToUpsertInput = (
  values: import("@/modules/clients/ClientCreateForm").ClientCreateFormValues,
  organizationMemberId: Identifier,
): LbsClientUpsertInput => ({
  organizationMemberId: values.organization_member_id ?? organizationMemberId,
  primary: {
    fullName: values.primary_full_name,
    emails: singleChannelFromValue(values.primary_email),
    phones: singleChannelFromValue(values.primary_phone),
  },
  business: {
    name: values.company_name,
    emails: cleanChannelFormValues(values.company_emails),
    phones: cleanChannelFormValues(values.company_phones),
    website: values.company_website,
    sector: values.company_sector,
    socialLinks: values.social_links,
    address: values.company_address,
    city: values.company_city,
    stateAbbr: values.company_state_abbr,
    zipcode: values.company_zipcode,
    country: values.company_country,
    notes: values.notes,
    size: values.company_size ?? undefined,
    revenue: values.company_revenue,
    taxIdentifier: values.tax_identifier,
    linkedinUrl: values.linkedin_url,
  },
  billing: {
    sameAsBusiness: values.billing_same_as_business,
    address: values.billing_address,
    city: values.billing_city,
    stateAbbr: values.billing_state_abbr,
    zipcode: values.billing_zipcode,
    country: values.billing_country,
    invoiceSameAsPrimary: values.invoice_same_as_primary,
    invoiceContactName: values.invoice_contact_name,
    invoiceEmail: values.invoice_email,
    invoicePhone: values.invoice_phone,
    contactIds: values.billing_contact_ids,
  },
});

export { splitClientFullName } from "@/modules/clients/clientFormUtils";
